import type {
  SharePointItem,
  SharePointSearchHit,
} from '@/lib/api/sharepoint.js';

import type { SharePointTreePosition } from './tree-position.js';

/**
 * One row a surface is offered, wherever it was found: the file or folder itself plus the site and library
 * it lives in. The address is part of the row rather than something the surface reads off its own position,
 * because a search result comes from anywhere the connection reaches and knows nothing about the level the
 * user happens to be standing on. Attaching it to a message, shelving it or opening it in SharePoint takes
 * the row and no further request.
 */
export interface SharePointRow {
  /**
   * Which SharePoint the row was found in. `null` means whichever connection the service resolves for
   * the workspace, which is what a surface showing a single tree has always meant. Every act on the row
   * addresses this connection, because Graph ids repeat between tenants: the same `drive_id` + `item_id`
   * elsewhere is either nothing or a different document.
   */
  connectionId: string | null;
  siteId: string;
  driveId: string;
  item: SharePointItem;
  /**
   * Where this row can be addressed in OUR tree, for a link a colleague follows — `null` when there is no
   * honest answer. Our link addresses a file as "this file, inside that folder", so a row that cannot name
   * its folder gets none: a link built anyway would open the library root and point at nothing.
   */
  treePosition: SharePointTreePosition | null;
}

/**
 * How a surface whose file rows are pick toggles reads and writes the batch. It answers about a ROW rather
 * than being handed a list of ids, because a pick is matched on the pair `drive_id` + `item_id` — Graph
 * makes an `item_id` unique only inside one library — and only the surface holds the batch to match against.
 */
export interface SharePointFileSelection {
  isSelected: (row: SharePointRow) => boolean;
  toggle: (row: SharePointRow) => void;
}

/**
 * The row for an item listed inside an open library. Its tree position is the one the Artifacts tab's links
 * have always carried: a folder is addressed as the folder to OPEN, a file as the file to point at inside
 * the folder it was listed in.
 */
export function treeItemRow(
  place: {
    connectionId: string | null;
    siteId: string;
    driveId: string;
    folderId: string | null;
  },
  item: SharePointItem,
): SharePointRow {
  return {
    connectionId: place.connectionId,
    siteId: place.siteId,
    driveId: place.driveId,
    item,
    treePosition: {
      siteId: place.siteId,
      driveId: place.driveId,
      folderId: item.isFolder ? item.itemId : place.folderId,
      itemId: item.isFolder ? null : item.itemId,
    },
  };
}

/**
 * The row for a search hit. A folder hit is its own address; a file hit is addressed inside the folder the
 * service named for it, which is the same shape a listed file gets. A hit Graph named no parent for keeps
 * no position at all, for the reason {@link SharePointRow.treePosition} gives.
 *
 * The connection comes from the REQUEST that found the hit — a search reaches everywhere one connection
 * goes, and nothing in the answer says which SharePoint answered.
 */
export function searchHitRow(
  hit: SharePointSearchHit,
  connectionId: string | null,
): SharePointRow {
  const place = { connectionId, siteId: hit.siteId, driveId: hit.driveId };
  if (hit.isFolder) {
    return {
      ...place,
      item: hit,
      treePosition: {
        siteId: hit.siteId,
        driveId: hit.driveId,
        folderId: hit.itemId,
        itemId: null,
      },
    };
  }
  return {
    ...place,
    item: hit,
    treePosition:
      hit.parentId === ''
        ? null
        : {
            siteId: hit.siteId,
            driveId: hit.driveId,
            folderId: hit.parentId,
            itemId: hit.itemId,
          },
  };
}
